import { useState } from 'react'
import { Button, Tag } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import type { BillingInvoice, BillingInvoiceLine, BillingInvoiceStatus } from '@/api/billing'
import {
  ADVICEPREP_BANDS,
  ADVICEPREP_ENTRY_CONNECTIONS,
  advicePrepDailyRate,
  formatAdvicePrepCurrency,
  type AdvicePrepBand,
} from '../pricing'
import AdvicePrepBillingExplainer from './AdvicePrepBillingExplainer'

const INVOICE_STATUS_TAG: Record<BillingInvoiceStatus, { label: string; color?: string }> = {
  upcoming: { label: 'Upcoming' },
  draft: { label: 'Draft' },
  open: { label: 'Unpaid', color: 'gold' },
  paid: { label: 'Paid', color: 'green' },
  uncollectible: { label: 'Failed', color: 'red' },
  void: { label: 'Void' },
}

const QUANTITY_PREFIX = /^\d+\s*×\s*/

type PeriodRow = {
  key: string
  label: string
  detail?: string
  amount: number
}

function formatDate(value: string | null) {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString()
}

function formatMoney(value: number | null) {
  if (value === null) return ''
  const formatted = formatAdvicePrepCurrency(Math.abs(value), 2)
  return value < 0 ? `-${formatted}` : formatted
}

// Stripe writes prorated rates into the description at full precision.
function formatDescription(description: string) {
  return description.replace(/\$(\d[\d,]*(?:\.\d+)?)/g, (_, amount: string) =>
    formatAdvicePrepCurrency(Number(amount.replace(/,/g, '')), 2)
  )
}

function connectionTier(line: BillingInvoiceLine): number | null {
  const match = /\bTier (\d+)\b/.exec(line.description)
  return match ? Number(match[1]) : null
}

function lineRate(line: BillingInvoiceLine): number | null {
  const match = /\(at \$(\d[\d,]*(?:\.\d+)?) \//.exec(line.description)
  const rate = match?.[1]
  return rate ? Number(rate.replace(/,/g, '')) : line.unitAmount
}

function bandForRate(rate: number | null) {
  if (rate === null || rate <= 0) return undefined
  return ADVICEPREP_BANDS.find(
    (band) => Math.abs(rate - band.rate) < 0.005 || Math.abs(rate - advicePrepDailyRate(band.rate)) < 0.005
  )
}

// The rate is the line's own evidence; the tier name only fills in when no rate matches.
// Stripe's tier 1 is the allowance inside the flat fee, so tier N is pricing band N - 2.
function lineBand(line: BillingInvoiceLine): AdvicePrepBand | undefined {
  if (line.kind !== 'usage') return undefined
  const tier = connectionTier(line)
  return bandForRate(lineRate(line)) ?? (tier === null ? undefined : ADVICEPREP_BANDS[tier - 2])
}

function bandRange(band: AdvicePrepBand) {
  return Number.isFinite(band.to) ? `${band.from} – ${band.to}` : `${band.from} or more`
}

function periodRows(lines: BillingInvoiceLine[]): PeriodRow[] {
  const rows: PeriodRow[] = []
  const bandRows = new Map<AdvicePrepBand, PeriodRow>()

  lines.forEach((line, index) => {
    if (line.amount === 0) return

    if (line.kind === 'flat' && QUANTITY_PREFIX.test(line.description)) {
      rows.push({
        key: `flat-${index}`,
        label: 'Flat fee',
        detail: `Covers your first ${ADVICEPREP_ENTRY_CONNECTIONS} connections`,
        amount: line.amount,
      })
      return
    }

    const band = lineBand(line)
    if (!band) {
      rows.push({
        key: `line-${index}`,
        label: formatDescription(line.description).replace(QUANTITY_PREFIX, ''),
        amount: line.amount,
      })
      return
    }

    const existing = bandRows.get(band)
    if (existing) {
      existing.amount = Math.round((existing.amount + line.amount) * 100) / 100
      return
    }
    const row: PeriodRow = {
      key: `band-${band.label}`,
      label: `Extra connections · ${band.label} rate`,
      detail: `${formatAdvicePrepCurrency(band.rate)} a month per connection, charged for each day your total was ${bandRange(band)}`,
      amount: line.amount,
    }
    bandRows.set(band, row)
    rows.push(row)
  })

  return rows
}

type AdvicePrepCurrentPeriodProps = {
  upcoming: BillingInvoice
  latest?: BillingInvoice | null
  cardLabel?: string | null
  connectionsOnMeter?: number
}

export default function AdvicePrepCurrentPeriod({
  upcoming,
  latest,
  cardLabel,
  connectionsOnMeter,
}: AdvicePrepCurrentPeriodProps) {
  const [explainerOpen, setExplainerOpen] = useState(false)
  const rows = periodRows(upcoming.lines)
  const showLatest = !!latest && latest.status !== 'draft'

  return (
    <div className="mt-5 rounded-xl border border-brand-200 bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <div className="font-display text-sm font-semibold text-brand-700">This billing period</div>
        <div className="text-xs text-gray-600">
          {formatDate(upcoming.periodStart)} – {formatDate(upcoming.periodEnd)}
        </div>
        <div className="grow" />
        {connectionsOnMeter !== undefined && (
          <span className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-800">
            {connectionsOnMeter} connection{connectionsOnMeter === 1 ? '' : 's'} on the meter
          </span>
        )}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-600">
              <th className="border-b border-gray-200 py-2 font-medium">Item</th>
              <th className="w-28 border-b border-gray-200 py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td className="border-b border-gray-100 py-2.5 align-top text-gray-900">
                  {row.label}
                  {row.detail && <div className="mt-0.5 text-xs text-gray-500">{row.detail}</div>}
                </td>
                <td className="border-b border-gray-100 py-2.5 text-right align-top text-gray-900">
                  {formatMoney(row.amount)}
                </td>
              </tr>
            ))}
            <tr>
              <td className="pt-2.5 text-right text-gray-600">Usage so far</td>
              <td className="pt-2.5 text-right font-semibold text-brand-700">{formatMoney(upcoming.usageAmount)}</td>
            </tr>
            {upcoming.tax !== 0 && (
              <tr>
                <td className="pt-1 text-right text-gray-600">GST</td>
                <td className="pt-1 text-right text-gray-900">{formatMoney(upcoming.tax)}</td>
              </tr>
            )}
            <tr>
              <td className="py-1 text-right font-semibold text-gray-900">Estimated total</td>
              <td className="py-1 text-right font-display text-lg font-bold text-gray-900">
                {formatMoney(upcoming.total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-600">
        <InfoCircleOutlined className="mt-1 text-brand-700" />
        <span>
          We'll charge your {cardLabel ?? 'card on file'} on {formatDate(upcoming.chargeDate)}. Your flat fee covers
          your first {ADVICEPREP_ENTRY_CONNECTIONS} connections. We count your connections every day, and each one
          after your first {ADVICEPREP_ENTRY_CONNECTIONS} is charged for that day at the rate for your total, so this
          estimate grows through the month.{' '}
          <Button
            type="link"
            size="small"
            className="h-auto p-0 text-xs font-semibold"
            onClick={() => setExplainerOpen(true)}
          >
            Want to know more?
          </Button>
        </span>
      </div>

      {showLatest && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 px-4 py-3 text-sm">
          <span className="text-gray-600">Last charge</span>
          <span className="font-semibold text-gray-900">{formatMoney(latest.total)}</span>
          <span className="text-gray-600">
            {formatDate(latest.chargeDate)} · {formatMoney(latest.subtotal - latest.usageAmount)} flat +{' '}
            {formatMoney(latest.usageAmount)} usage{latest.tax !== 0 && ` + ${formatMoney(latest.tax)} GST`}
          </span>
          <Tag className="m-0" color={INVOICE_STATUS_TAG[latest.status].color}>
            {INVOICE_STATUS_TAG[latest.status].label}
          </Tag>
        </div>
      )}

      <AdvicePrepBillingExplainer open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </div>
  )
}
